// src/pages/mallkiosk/MallKioskPage.js
// Full-screen multi-vendor entrance kiosk for food courts (large-format touch).
// Separate CUSTOMER MODE surface from QRMallKioskPage.js (phone QR page).
// Reuses CartContext-family (MultiCartContext), PaymentModal, ModifierSelector,
// IdleScreen pattern, PromoCode, applyTheme, and the session-checkout flow
// already proven in MultiVendorCheckout.js (cart split by vendor, real orders
// created before payment).

import React, { useState, useEffect, useCallback, useRef } from 'react';
import ConfirmModal from '../../components/ConfirmModal';
import { useLanguage }        from '../../context/LanguageContext';
import { useMultiCart }       from '../../context/MultiCartContext';
import api                    from '../../services/api';
import { createSession, checkout } from '../../services/session';
import { applyTheme }         from '../../utils/applyTheme';

import AttractScreen     from './AttractScreen';
import LanguageScreen    from './LanguageScreen';
import DietaryScreen     from './DietaryScreen';
import StallSelectScreen from './StallSelectScreen';
import VendorMenuScreen  from './VendorMenuScreen';
import CartReviewScreen  from './CartReviewScreen';
import SuccessScreen     from './SuccessScreen';

const IDLE_WARN_AFTER = 90 * 1000; // no interaction
const IDLE_WARN_SECS  = 10;        // countdown before hard reset

export default function MallKioskPage() {
  const { isAr, setLang } = useLanguage();
  const { carts, totals, clearCart } = useMultiCart();

  const slug = window.location.pathname.split('/kiosk/mall/')[1]?.split('?')[0]?.split('/')[0] || null;

  const [confirm, setConfirm] = useState(null); // { message, onConfirm } for ConfirmModal
  const [screen,         setScreen]         = useState('attract');
  const [mall,           setMall]           = useState(null);
  const [vendors,        setVendors]        = useState([]);
  const [loading,        setLoading]        = useState(true);
  const [error,          setError]          = useState('');
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [session,        setSession]        = useState(null);
  const [diet,           setDiet]           = useState('none'); // session-only dietary preference
  const [lastOrders,     setLastOrders]     = useState([]);
  const [idleWarning,    setIdleWarning]    = useState(false);
  const [idleCountdown,  setIdleCountdown]  = useState(IDLE_WARN_SECS);

  const idleTimerRef = useRef(null);

  // ── Load mall + vendors ───────────────────────────────────
  useEffect(() => {
    if (!slug) { setError('Invalid kiosk link.'); setLoading(false); return; }
    api.get(`/public/mall/${slug}/vendors`)
      .then(r => {
        setMall(r.data.mall);
        setVendors(r.data.vendors || []);
        applyTheme(r.data.mall?.kioskTheme || 'dark', r.data.mall?.brandColor, r.data.mall?.bgColor, r.data.mall?.tileColor, r.data.mall?.fontColor, r.data.mall?.buttonTextColor, r.data.mall?.surfaceColor);
      })
      .catch(err => setError(err.response?.data?.error || 'Could not load kiosk.'))
      .finally(() => setLoading(false));
    return () => applyTheme('dark');
  }, [slug]);

  // ── Reset everything back to the attract loop ─────────────
  const resetToAttract = useCallback(() => {
    clearCart();
    setSelectedVendor(null);
    setSession(null);
    setDiet('none');
    setLastOrders([]);
    setIdleWarning(false);
    setIdleCountdown(IDLE_WARN_SECS);
    setScreen('attract');
  }, [clearCart]);

  // ── Idle detection (active on every screen except attract) ─
  useEffect(() => {
    if (screen === 'attract') { setIdleWarning(false); return; }

    let warnTimer = setTimeout(() => setIdleWarning(true), IDLE_WARN_AFTER);
    const bump = () => {
      if (idleWarning) return; // ignore taps once the warning overlay is up — it has its own buttons
      clearTimeout(warnTimer);
      warnTimer = setTimeout(() => setIdleWarning(true), IDLE_WARN_AFTER);
    };
    const events = ['mousedown', 'touchstart', 'keydown'];
    events.forEach(e => document.addEventListener(e, bump, { passive: true }));
    return () => { clearTimeout(warnTimer); events.forEach(e => document.removeEventListener(e, bump)); };
  }, [screen, idleWarning]);

  // ── Idle warning countdown → hard reset ────────────────────
  useEffect(() => {
    if (!idleWarning) return;
    setIdleCountdown(IDLE_WARN_SECS);
    const t = setInterval(() => {
      setIdleCountdown(c => {
        if (c <= 1) { clearInterval(t); resetToAttract(); }
        return c - 1;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [idleWarning, resetToAttract]);

  // ── Cancel / start over (with confirm if cart has items) ──
  const handleCancelOrder = () => {
    if (totals.totalItems > 0) {
      setConfirm({
        message: isAr ? 'هل تريد إلغاء الطلب الحالي؟' : 'Cancel current order and start over?',
        confirmLabel: isAr ? 'نعم، إلغاء' : 'Yes, cancel',
        cancelLabel: isAr ? 'رجوع' : 'Keep order',
        dir: isAr ? 'rtl' : 'ltr',
        onConfirm: resetToAttract,
      });
      return;
    }
    resetToAttract();
  };

  const handleStart        = () => setScreen('language');
  const handleLanguagePick = (lang) => { setLang(lang); setScreen('dietary'); };
  const handleDietPick     = (pref) => { setDiet(pref || 'none'); setScreen('stalls'); };
  const handleSelectVendor = (vendorId) => { setSelectedVendor(vendorId); setScreen('menu'); };
  const handleBackToStalls = () => setScreen('stalls');
  const handleOpenCart     = () => setScreen('cart');

  // ── Ensure a session exists, then split-checkout by vendor ─
  const handlePlaceOrders = useCallback(async (customerPhone) => {
    const cartsByVendor = {};
    for (const [vendorId, items] of Object.entries(carts)) {
      if (items.length) {
        cartsByVendor[vendorId] = items.map(i => ({
          menuItemId:       i.id,
          quantity:         i.qty,
          modifierNote:     i.modifierNote || '',
          bundleComponents: i.bundleComponentsDetail || undefined,
        }));
      }
    }
    if (!Object.keys(cartsByVendor).length) throw new Error('No items in cart');

    let activeSession = session;
    if (!activeSession) {
      const res = await createSession(mall.id);
      activeSession = res.data;
      setSession(activeSession);
    }

    const res = await checkout(activeSession.id, {
      cartsByVendor,
      sourceKiosk:   'MALL_KIOSK',
      customerPhone: customerPhone || undefined,
    });
    // Return full result so CartReviewScreen can read serviceFee + paymentTotal
    return res.data;
  }, [carts, mall, session]);

  const handleOrdersPlaced = (orders) => setLastOrders(orders);

  const currentVendorData = vendors.find(vd => vd.vendor.id === selectedVendor);

  if (loading) return (
    <div style={{ height:'100vh', width:'100vw', background:'var(--bg)', display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div style={{ color:'var(--text2)', fontSize:24 }}>Loading kiosk...</div>
    </div>
  );

  if (error) return (
    <div style={{ height:'100vh', width:'100vw', background:'var(--bg)', display:'flex', alignItems:'center', justifyContent:'center', padding:40, textAlign:'center' }}>
      <div>
        <div style={{ fontSize:72, marginBottom:24 }}>😕</div>
        <div style={{ fontSize:28, fontWeight:700, color:'var(--red)', marginBottom:12 }}>Unavailable</div>
        <div style={{ fontSize:18, color:'var(--text2)' }}>{error}</div>
      </div>
    </div>
  );

  return (
    <div style={{ position:'fixed', inset:0, width:'100vw', height:'100vh', overflow:'hidden', background:'var(--bg)' }}>
      {confirm && <ConfirmModal {...confirm} onCancel={() => setConfirm(null)} />}

      {screen === 'attract' && (
        <AttractScreen mall={mall} onStart={handleStart} />
      )}

      {screen === 'language' && (
        <LanguageScreen mall={mall} onPick={handleLanguagePick} />
      )}

      {screen === 'dietary' && (
        <DietaryScreen mall={mall} isAr={isAr} initial={diet}
          onContinue={handleDietPick} onSkip={() => handleDietPick('none')} />
      )}

      {screen === 'stalls' && (
        <StallSelectScreen
          mall={mall}
          vendors={vendors}
          carts={carts}
          totals={totals}
          onSelectVendor={handleSelectVendor}
          onOpenCart={handleOpenCart}
          onCancelOrder={handleCancelOrder}
        />
      )}

      {screen === 'menu' && currentVendorData && (
        <VendorMenuScreen
          vendorData={currentVendorData}
          totals={totals}
          diet={diet}
          onClearDiet={() => setDiet('none')}
          onBack={handleBackToStalls}
          onOpenCart={handleOpenCart}
          onCancelOrder={handleCancelOrder}
        />
      )}

      {screen === 'cart' && (
        <CartReviewScreen
          mall={mall}
          vendors={vendors}
          carts={carts}
          totals={totals}
          onBack={() => setScreen(selectedVendor ? 'menu' : 'stalls')}
          onAddMore={() => setScreen('stalls')}
          onCancelOrder={handleCancelOrder}
          placeOrders={handlePlaceOrders}
          onOrdersPlaced={handleOrdersPlaced}
          onSuccess={() => setScreen('success')}
        />
      )}

      {screen === 'success' && (
        <SuccessScreen orders={lastOrders} total={totals.grandTotal} onReset={resetToAttract} />
      )}

      {/* Idle "still there?" overlay — shown over any non-attract screen */}
      {idleWarning && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.88)', zIndex:5000, display:'flex', alignItems:'center', justifyContent:'center', textAlign:'center', padding:40 }}>
          <div>
            <div style={{ fontSize:64, marginBottom:20 }}>👋</div>
            <div style={{ fontSize:36, fontWeight:800, color:'var(--text)', marginBottom:14 }}>
              {isAr ? 'هل ما زلت هناك؟' : 'Still there?'}
            </div>
            <div style={{ fontSize:20, color:'var(--text2)', marginBottom:36 }}>
              {isAr ? `سيتم إعادة التشغيل خلال ${idleCountdown}...` : `Returning to start in ${idleCountdown}...`}
            </div>
            <button
              onClick={() => setIdleWarning(false)}
              style={{ padding:'20px 56px', fontSize:24, fontWeight:800, borderRadius:18, background:'var(--accent)', color:'var(--button-text)', border:'none', cursor:'pointer' }}>
              {isAr ? 'متابعة الطلب' : 'Keep Ordering'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
